import type {
  BaseCommiteeBank,
  BaseCommiteeUser,
  BaseCommiteeWorkplace,
  CommiteeWithStatus,
} from '@cpce/shared';
import { useSession } from '@/api/auth';
import { commiteeQuery, query } from '@/api/queries.utils';
import { useCommiteeId, useCommiteeIdFromUrl } from '@/utils/router';
import { commiteeQueryKeys } from './commitee.query-keys';

export type Commitee = CommiteeWithStatus;
export type CommiteeUser = BaseCommiteeUser;
export type CommiteeBank = BaseCommiteeBank;
export type CommiteeWorkplace = BaseCommiteeWorkplace;

export function useCommitees() {
  return query<Commitee[]>({
    queryKey: commiteeQueryKeys.all,
    url: '/commitees',
    // Every commitee-scoped page resolves its id from this list: without a stale time each newly
    // mounted page or modal refetched it (~100 KB). Commitee mutations invalidate it.
    staleTime: 5 * 60_000,
  });
}

export function useUserCommitee() {
  const { data: session } = useSession();
  return query<Commitee | null>({
    queryKey: commiteeQueryKeys.commitee,
    url: '/users/commitee',
    enabled: !!session,
  });
}

export function useCommitee(id: number) {
  return query<Commitee>({
    queryKey: [...commiteeQueryKeys.all, id],
    url: `/commitees/${id}`,
  });
}

export function useSlugCommitee() {
  const commiteeId = useCommiteeIdFromUrl();
  return query<Commitee>({
    queryKey: [...commiteeQueryKeys.all, commiteeId ?? 0],
    url: `/commitees/${commiteeId}`,
    enabled: commiteeId !== undefined,
  });
}

export function useCommiteeUsersWithGuard() {
  const commiteeId = useCommiteeId();

  if (!commiteeId) {
    return {
      data: [],
      isLoading: false,
      isError: false,
      error: null,
    };
  }

  return query<CommiteeUser[]>({
    queryKey: [...commiteeQueryKeys.all, 'users', commiteeId],
    url: `/commitees/${commiteeId}/users`,
    enabled: !!commiteeId,
  });
}

export function useCommiteeBanks(commiteeId: number) {
  return query<CommiteeBank[]>({
    queryKey: [...commiteeQueryKeys.all, 'banks', commiteeId],
    url: `/commitees/${commiteeId}/banks`,
  });
}

// `commiteeQuery`, not `query`: only it fills `:commiteeId` from the route slug, which also covers
// an admin browsing a CSE's space.
export function useCurrentCommiteeBanks() {
  return commiteeQuery<CommiteeBank[]>({
    queryKey: [...commiteeQueryKeys.all, 'banks', 'current'],
    url: '/commitees/:commiteeId/banks',
  });
}

export function useCommiteeWorkplaces(commiteeId: number) {
  return query<CommiteeWorkplace[]>({
    queryKey: [...commiteeQueryKeys.all, 'workplaces', commiteeId],
    url: `/commitees/${commiteeId}/workplaces`,
  });
}
